// docHistory.mjs — 文档改动历史面的纯逻辑（第五十五批 §5.3）。无 React、无 DOM。
//
// 两件事必须离开 JSX 才可测，故落在这里（房屋风格：pages/assetView.mjs 同款）：
//
// 1) unified diff 的切分。之所以在这里切成**受控数据**再由组件渲染成 <span>，
//    而不是拼一串带颜色的 HTML 塞进 dangerouslySetInnerHTML：diff 正文原样携带
//    文档源码，源码里出现 `<script>` 是完全合法的 markdown 内容——发布管线的
//    反注入不变式（OQ-A14 已裁不得放宽）在历史面同样成立。
// 2) 内存缓存。抽屉随文档切换反复卸载重挂，放在组件 state 里每次都是空的，
//    两篇文档来回点一次就是一次全量重拉（列表 + 每条明细）。故缓存必须是模块级，
//    生命周期与页面同寿；刷新页面即失效（§5.3 明确不写 localStorage）。

export const PAGE_SIZE = 20;              // §5.3：每页 20 条，倒序

// ── diff 行的四类（外加 meta：文件头与 "\ No newline" 一类的非内容行） ─────────
export const DIFF_META = 'meta';
export const DIFF_HUNK = 'hunk';
export const DIFF_ADD = 'add';
export const DIFF_DEL = 'del';
export const DIFF_CONTEXT = 'context';

// 判定单行的类型。`beforeFirstHunk` 是关键的一条：markdown 里 `---` 是分隔线，
// 删掉它得到的 diff 行是 `----`，若无脑按 "以 --- 开头即文件头" 判定就会把一条
// 真删除行渲染成灰色的元信息。内容行只可能出现在 hunk 之内，故第一个 `@@` 之前
// 的一切一律算 meta，之后才按首字符分类。
export function classifyDiffLine(line, { beforeFirstHunk = false } = {}) {
  const s = typeof line === 'string' ? line : '';
  if (s.startsWith('@@')) return DIFF_HUNK;
  if (beforeFirstHunk) return DIFF_META;      // `diff --git` / `--- a/…` / `+++ b/…` / index …
  if (s.startsWith('\\')) return DIFF_META;   // "\ No newline at end of file"
  if (s.startsWith('+')) return DIFF_ADD;
  if (s.startsWith('-')) return DIFF_DEL;
  return DIFF_CONTEXT;                        // ' ' 前缀的上下文，以及空行
}

// unified diff 正文 → [{ type, text }]。
// 空 diff（后端裁掉了正文，或本条本就无正文）→ 空数组，调用侧据此显示"无正文"。
export function splitDiffLines(raw) {
  const text = typeof raw === 'string' ? raw : '';
  if (text === '') return [];
  const rows = text.split(/\r?\n/);
  // 末尾换行会切出一个尾随空串；它不是一行空白上下文，删掉。
  // 反过来，**无尾换行**的 diff 一行都不能少——所以只砍这一个，且只在它是空串时砍。
  if (rows.length > 1 && rows[rows.length - 1] === '') rows.pop();
  let seenHunk = false;
  return rows.map((line) => {
    const type = classifyDiffLine(line, { beforeFirstHunk: !seenHunk });
    if (type === DIFF_HUNK) seenHunk = true;
    return { type, text: line };
  });
}

// ── 展示助手 ────────────────────────────────────────────────────────────────
// +12 / −3。减号用 U+2212 而非 ASCII 连字符：与 diff 正文里的 '-' 前缀区分开，
// 也让两个数字在等宽字体下对齐。
export function formatDelta(added, removed) {
  const n = (v) => (Number.isFinite(Number(v)) ? Math.max(0, Math.trunc(Number(v))) : 0);
  return { add: `+${n(added)}`, del: `−${n(removed)}` };
}

// 把攥在组件手里的 offset 收回到"最后一页的页首"。
// 之所以要单独一条判据：total 会缩（重导入、条目被裁、换了个权限更小的会话），
// offset 却还停在旧位置。旧写法只把 offset 夹到 tot，from = off + 1 于是变成
// tot + 1，渲染出「第 38–37 条，共 37 条」这种倒挂区间。夹到页首才既不倒挂、
// 又还能让"更近"把用户带回去。
export function clampOffset(total, offset, limit) {
  const tot = Math.max(0, Number(total) || 0);
  const lim = Math.max(1, Number(limit) || PAGE_SIZE);
  const off = Math.max(0, Number(offset) || 0);
  if (tot === 0) return 0;
  const lastPageStart = Math.floor((tot - 1) / lim) * lim;
  return Math.min(off, lastPageStart);
}

// 倒序翻页的窗口描述。total=0 时 from/to 都是 0（调用侧走"空"分支）。
export function pageRange({ total, offset, limit }) {
  const tot = Math.max(0, Number(total) || 0);
  const lim = Math.max(1, Number(limit) || PAGE_SIZE);
  const off = clampOffset(tot, offset, lim);
  if (tot === 0) return { from: 0, to: 0, hasOlder: false, hasNewer: false };
  return {
    from: off + 1,
    to: Math.min(off + lim, tot),
    hasOlder: off + lim < tot,   // "更早" = 往下一页（列表是新→旧）
    hasNewer: off > 0,
  };
}

// 后端 §4.2 的响应可能带 null 字段（开关关闭时 entries: []），统一成一个形状，
// 免得组件里到处 `?? []`。
export function normalizeHistory(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    enabled: d.enabled === true,
    total: Math.max(0, Number(d.total) || 0),
    limit: Math.max(1, Number(d.limit) || PAGE_SIZE),
    offset: Math.max(0, Number(d.offset) || 0),
    entries: Array.isArray(d.entries) ? d.entries : [],
  };
}

// 文档头部两个可选块的显示判据（§5.2）。之所以是纯函数而不是 JSX 里的两个 &&：
// 这两条各自对应一个具体的丑陋失败——updatedAtMs 为 null（文件从未提交）时若照渲
// 不误就会显示 1970 年；historyEnabled 缺失（后端未升级）时若按真值判断就会点亮
// 一个必然 404 的入口。有判据才不会在下一次改版里被顺手写回去。
export function docHeaderFlags(content) {
  const c = content && typeof content === 'object' ? content : {};
  const ms = Number(c.updatedAtMs);
  const showUpdated = Number.isFinite(ms) && ms > 0;
  return {
    showUpdated,
    updatedAtMs: showUpdated ? ms : null,
    updatedRef: showUpdated && c.updatedRef ? String(c.updatedRef) : '',
    showHistory: c.historyEnabled === true,
  };
}

// 头部那行"更新于 …"只显示到**日**。完整的 `2026年7月30日 20:03` 太长：第五十五批
// 往标题行右侧塞进了更新时间与"修改历史"两个新孩子，整行从约 208px 涨到约 466px，
// 而 360px 视口下这一行只有约 296px 可用——时分挪进 tooltip（连同短 sha），行里只留
// 日期，是把这一行拉回可换行范围最省事的一半。
// 非法/缺失值返回空串（调用侧本就有 showUpdated 判据，这里只是不许它渲染出 1970）。
export function fmtUpdatedAt(ms, lang) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  try {
    return new Date(n).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return '';
  }
}

// 文档标题行右侧那一栏（可用语言 / 更新时间 / 修改历史 / 提改）的布局。
// 之所以从纯模块导出而不是就地写在 JSX 的 sx 里：它承载一条会被顺手写回去的不变式
// ——**这里不得出现 flexShrink: 0**。第五十五批加的 `flexWrap: 'wrap'` 与它同在时
// 是死的：flex 项的基准尺寸取 max-content，而换行容器的 max-content 就是"全挤在
// 一行"的宽度；flex-shrink: 0 又禁止缩到基准以下，于是永远轮不到换行。结果是
// 360px 视口下 466px 的一行硬顶进 296px，祖先链上没有任何一层裁剪它，页面 body
// 就出现横向滚动条（本仓明令禁止）。去掉 flexShrink 后这一栏可缩到"最宽的那个
// 孩子"（各孩子自带 whiteSpace: nowrap，故不会被拦腰截断），余下的自然折到第二行。
// useFlexGap: MUI 的 Stack 默认用 margin 排间距，换行后第二行首项会多出一截左外边距
// 且两行之间毫无行距；改用 gap 才是真的能换行的排布。
export const DOC_HEADER_META_PROPS = {
  direction: 'row',
  spacing: 1.5,
  useFlexGap: true,
  // alignItems 必须在 sx 里：MUI v6 起 Stack 不收 system props（DP-17）——写在
  // 对象顶层经 {...spread} 传入与写成 JSX 属性同罪，且 mui-system-props 判据
  // 只扫得到 JSX 属性形态，这里靠测试断言钉住。
  sx: { alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
};

// ── 抽屉的每文档状态 ────────────────────────────────────────────────────────
export function scopeKey(group, docKey) {
  return `${group}|${docKey}`;
}

export function emptyDrawerState(group, docKey) {
  return { scope: scopeKey(group, docKey), offset: 0, openId: null, diffs: {} };
}

// 换文档（或换组）时把状态**派生**回第一页，而不是在 effect 里 setOffset(0)。
// 被踩过的坑：同一次提交里，effect 中的 setState 对**紧接着跑的下一个 effect**
// 不可见。重置 effect 刚把 offset 归零，加载 effect 立刻就用 (新 docKey, 旧 offset)
// 跑了一发——一次谁也没要的往返，而且响应会写进 `group|新 docKey|旧 offset` 这个
// 键，缓存里从此躺着一页从没被请求过的数据（下次真翻到那一页还会直接命中它）。
// 派生值不可能滞后：scope 一变，同一次渲染里读到的 offset 就已经是 0。
export function reconcileDrawerState(prev, group, docKey) {
  const scope = scopeKey(group, docKey);
  if (prev && prev.scope === scope) return prev;   // 同一文档：原样返回（引用相等 ⇒ 不多渲染一次）
  return emptyDrawerState(group, docKey);
}

// ── 缓存 ────────────────────────────────────────────────────────────────────
// 键：列表按 (group, docKey, offset)；明细按 changeId（changeId 是库主键，全局唯一）。
export function historyPageKey(group, docKey, offset) {
  return `${group}|${docKey}|${Math.max(0, Number(offset) || 0)}`;
}
export function changeKey(changeId) {
  return `chg:${changeId}`;
}

// 有界 LRU。之所以有界：diff 正文是大文本，一个长会话里翻遍全库会把它攒成
// 几十 MiB 常驻——"内存缓存"不该变成内存泄漏。Map 天然保序，读命中时重新插入
// 即为"最近使用"。
export function createCache(limit) {
  const max = Math.max(1, Number(limit) || 1);
  const map = new Map();
  return {
    get limit() { return max; },
    get size() { return map.size; },
    has: (k) => map.has(k),
    get(k) {
      if (!map.has(k)) return undefined;
      const v = map.get(k);
      map.delete(k);
      map.set(k, v);               // 触碰即刷新 LRU 次序
      return v;
    },
    set(k, v) {
      if (map.has(k)) map.delete(k);
      map.set(k, v);
      while (map.size > max) map.delete(map.keys().next().value);
      return v;
    },
    delete: (k) => map.delete(k),
    clear: () => map.clear(),
    keys: () => [...map.keys()],
  };
}

// 只有"这一发请求仍然是当前那一发"时，响应才准进缓存。
// 旧写法在 `if (alive)` **之前**就 cache.set：被作废的请求（切文档、连点翻页、
// 抽屉关闭卸载）照样把结果落进缓存，于是缓存里存着一页没人要的数据，而它
// 之后还会被当成命中直接渲染。缓存写入必须与"这个响应我们还认不认"同一道闸。
export function cachePageIfAlive(cache, key, data, alive) {
  if (!alive) return false;
  cache.set(key, data);
  return true;
}

// 页面级单例。列表条目轻（20 行元数据），明细重（diff 正文），故上限不同。
export const HISTORY_CACHE_MAX = 40;
export const DIFF_CACHE_MAX = 60;
export const historyCache = createCache(HISTORY_CACHE_MAX);
export const diffCache = createCache(DIFF_CACHE_MAX);
